import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import Input from "../../Shared/Input";
import FormFooter from "../../Shared/FormFooter";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import AlertRed from "../../Shared/AlertRed";
import PrimaryButton from "../../Shared/PrimaryButton";
import AlertModal from "../../Shared/AlertModal";
import {
  getAccountSettings,
  updateAccountSettings,
} from "../../../api/settingsApi";
import {
  resendEmailChangeVerification,
  cancelEmailChange,
} from "../../../api/authApi";

const PersonalInformationFormContent = ({ userData }) => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const location = useLocation();
  const queryClient = useQueryClient();

  // Check if user just returned from email verification
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("emailChanged") === "true") {
      // Invalidate and refetch account settings to get updated data
      queryClient.invalidateQueries({ queryKey: ["accountSettings"], exact: true });

      setSuccess({
        isEmailChange: false,
        message: "Email address successfully updated!",
      });
      setError(null); // Clear any existing errors

      // Clear the URL parameter
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location, queryClient]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      firstName: userData.firstName || "",
      lastName: userData.lastName || "",
      email: userData.email || "",
    },
  });

  // Update mutation
  const { mutate: updateSettings, isPending: isUpdating } = useMutation({
    mutationFn: updateAccountSettings,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["accountSettings"], exact: true });
      setError(null);
      setSuccess({
        isEmailChange: response.emailChangeRequested || false,
        message:
          response.message || "Personal information updated successfully",
      });
    },
    onError: (err) => {
      setError({
        title: "Error",
        message:
          err.response?.data?.error?.message ||
          "Failed to update personal information",
      });
    },
  });

  const onSubmit = async (data) => {
    // Don't save if nothing has changed
    if (!isDirty) {
      return;
    }

    setError(null);
    setSuccess(null);
    updateSettings(data);
  };


  const handleResendVerification = async () => {
    if (!userData.pendingEmail || isResending) return;

    setIsResending(true);
    try {
      const response = await resendEmailChangeVerification();
      setSuccess({
        isEmailChange: true,
        message:
          response.message ||
          `Verification email resent to ${userData.pendingEmail}`,
      });
      setError(null);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        "Failed to resend verification email. Please try again.";
      setError({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleCancelEmailChange = async () => {
    setIsCancelling(true);
    try {
      await cancelEmailChange();
      // Invalidate and refetch account settings to get updated data
      queryClient.invalidateQueries({ queryKey: ["accountSettings"], exact: true });
      setSuccess({
        isEmailChange: false,
        message: "Email change cancelled successfully",
      });
      setError(null);
      setShowCancelConfirm(false);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        "Failed to cancel email change. Please try again.";
      setError({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div className="sm:col-span-3">
          <Input
            id="firstName"
            name="firstName"
            label="First name"
            errormessage={errors?.firstName?.message}
            {...register("firstName", {
              onBlur: (e) => {
                setValue("firstName", e.target.value.trim());
              },
              required: "First name is required",
            })}
          />
        </div>

        <div className="sm:col-span-3">
          <Input
            id="lastName"
            name="lastName"
            label="Last name"
            errormessage={errors?.lastName?.message}
            {...register("lastName", {
              onBlur: (e) => {
                setValue("lastName", e.target.value.trim());
              },
              required: "Last name is required",
            })}
          />
        </div>

        <div className="col-span-full">
          <Input
            id="email"
            name="email"
            label="Email address"
            errormessage={errors?.email?.message}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                message: "Invalid email address",
              },
            })}
          />
          {userData.pendingEmail && (
            <div className="mt-2 p-3 bg-signal-amber-50 border border-signal-amber-100 rounded-md">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <p className="text-sm text-signal-amber-600">
                    <strong>Pending email change:</strong>{" "}
                    {userData.pendingEmail}
                  </p>
                  <p className="text-xs text-signal-amber-600 mt-1">
                    Check your email and click the verification link to complete
                    the change.
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <PrimaryButton
                    type="button"
                    onClick={handleResendVerification}
                    disabled={isResending}
                    colour="amber"
                    className="text-xs px-2 py-1"
                  >
                    {isResending ? "Sending..." : "Resend"}
                  </PrimaryButton>
                  <PrimaryButton
                    type="button"
                    onClick={() => setShowCancelConfirm(true)}
                    disabled={isCancelling}
                    colour="gray"
                    className="text-xs px-2 py-1"
                  >
                    Cancel
                  </PrimaryButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <FormFooter
        primaryCaption="Save"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />
      {error ? (
        <div className="mt-4">
          <AlertRed title={error.title} message={error.message} />
        </div>
      ) : null}
      {success && (
        <div className="mt-4">
          <div className="rounded-md bg-signal-green-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-accent-700">
                  {success.message}
                </h3>
                {success.isEmailChange && (
                  <p className="text-xs text-signal-green-600 mt-1">
                    Please check your inbox and click the verification link to
                    complete the email change.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Email Change Confirmation Modal */}
      <AlertModal
        isOpen={showCancelConfirm}
        modalTitle="Cancel Email Change"
        modalMessage={
          <>
            Are you sure you want to cancel the email change to{" "}
            <strong>{userData.pendingEmail}</strong>? You will need to start the
            process again if you want to change your email later.
          </>
        }
        primaryCaption="Yes, Cancel Change"
        primaryButtonColour="red"
        secondaryCaption="Keep Change"
        primaryClickHandler={handleCancelEmailChange}
        dismissModalHandler={() => setShowCancelConfirm(false)}
        showIcon={true}
      />
    </TabFormWrapper>
  );
};

const PersonalInformationForm = () => {
  // Fetch user data
  const { data: userData, isLoading } = useQuery({
    queryKey: ["accountSettings"],
    queryFn: getAccountSettings,
  });

  if (isLoading) {
    return (
      <TabFormWrapper>
        <div className="flex justify-center items-center py-8">
          <div className="text-ink-500">Loading...</div>
        </div>
      </TabFormWrapper>
    );
  }

  if (!userData?.user) {
    return (
      <TabFormWrapper>
        <div className="flex justify-center items-center py-8">
          <div className="text-ink-500">No user data available</div>
        </div>
      </TabFormWrapper>
    );
  }

  return <PersonalInformationFormContent userData={userData.user} />;
};

export default PersonalInformationForm;
